import { CURRENCY_CODE_OPTIONS, type CurrencyCode } from '@klear/constants/currency'
import { Money } from '@klear/money'
import { Combobox } from '@klear/ui/components/combobox'
import { Input } from '@klear/ui/components/input'
import { Label } from '@klear/ui/components/label'
import { cn } from '@klear/ui/lib/utils'
import type { LucideIcon } from 'lucide-react'
import { forwardRef, useCallback } from 'react'

export interface MoneyInputProps {
  value: Money
  onChange: (value: Money) => void
  label?: string
  placeholder?: string
  disabled?: boolean
  lockCurrency?: boolean
  currencyDisabled?: boolean
  max?: Money
  allowNegative?: boolean
  icon?: LucideIcon
  helperText?: string
  errorMessage?: string
  className?: string
  id?: string
  inputClassName?: string
  inputContainerClassName?: string
  onBlur?: () => void
}

function parseMinorInput(input: string, allowNegative: boolean): bigint {
  const digitsOnly = input.replace(/[^\d]/g, '')
  if (digitsOnly === '') return 0n
  const negative = allowNegative && input.trim().startsWith('-')
  const minor = BigInt(digitsOnly)
  return negative ? -minor : minor
}

export function clampMoneyToMax(next: Money, max: Money | undefined): Money {
  if (max == null || max.currency !== next.currency) return next
  return next.greaterThan(max) ? max : next
}

export const MoneyInput = forwardRef<HTMLInputElement, MoneyInputProps>(
  (
    {
      value,
      onChange,
      label = 'Amount',
      placeholder = '0.00',
      disabled = false,
      lockCurrency = false,
      currencyDisabled = false,
      max,
      allowNegative = false,
      icon: Icon,
      helperText,
      errorMessage,
      className,
      id,
      inputClassName,
      inputContainerClassName,
      onBlur
    },
    ref
  ) => {
    const handleAmountChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        onChange(
          clampMoneyToMax(Money.fromMinorUnits(parseMinorInput(event.target.value, allowNegative), value.currency), max)
        )
      },
      [onChange, max, value.currency, allowNegative]
    )

    const handleCurrencyChange = useCallback(
      (next: string) => {
        onChange(clampMoneyToMax(Money.fromMinorUnits(value.amount, (next || value.currency) as CurrencyCode), max))
      },
      [onChange, max, value.amount, value.currency]
    )

    return (
      <div className={cn('space-y-2', className)}>
        {label ? (
          <Label className="flex items-center gap-2 text-sm font-medium">
            {Icon && <Icon className="h-4 w-4" />}
            {label}
          </Label>
        ) : null}
        <div className="flex flex-wrap gap-2">
          <Input
            ref={ref}
            id={id}
            type="text"
            containerClassName={inputContainerClassName}
            className={cn('min-w-[7rem] flex-1', inputClassName)}
            placeholder={placeholder}
            disabled={disabled}
            value={value.toString()}
            onChange={handleAmountChange}
            onBlur={onBlur}
          />
          {lockCurrency ? null : (
            <Combobox
              className="w-[10.5rem] shrink-0"
              value={value.currency}
              options={CURRENCY_CODE_OPTIONS}
              placeholder="CCY"
              triggerLabel="Currency"
              disabled={disabled || currencyDisabled}
              onChange={handleCurrencyChange}
            />
          )}
        </div>
        {errorMessage ? <p className="text-sm text-destructive">{errorMessage}</p> : null}
        {helperText ? <p className="text-sm text-muted-foreground">{helperText}</p> : null}
      </div>
    )
  }
)

MoneyInput.displayName = 'MoneyInput'
